/**
 * Pure helpers for choosing and describing Open House events.
 * Shared by the public Open House page and the admin editor so both agree on
 * which event is "featured" and how its status/time reads.
 */

export const OPEN_HOUSE_TIMEZONE = "America/Los_Angeles";
export const DEFAULT_OPEN_HOUSE_CONTACT_EMAIL = "silversagehomeschoolhubs@gmail.com";

/** Statuses the public may see (must match the public read policy). */
export const PUBLIC_OPEN_HOUSE_STATUSES = [
  "registration_open",
  "registration_closed",
  "postponed",
  "cancelled",
] as const;

/** The three statuses admins choose from. "Scheduled" = RSVPs open. */
export const ADMIN_STATUS_OPTIONS = [
  { value: "registration_open", label: "Scheduled" },
  { value: "postponed", label: "Postponed" },
  { value: "cancelled", label: "Cancelled" },
] as const;

export function adminStatusLabel(status: string): string {
  switch (status) {
    case "registration_open": return "Scheduled";
    case "registration_closed": return "Scheduled (RSVPs closed)";
    case "postponed": return "Postponed";
    case "cancelled": return "Cancelled";
    case "draft": return "Draft";
    case "completed": return "Completed";
    case "archived": return "Archived";
    default: return status;
  }
}

/** Today's calendar date (YYYY-MM-DD) in the given timezone. */
export function todayInTimezone(now: Date = new Date(), timeZone = OPEN_HOUSE_TIMEZONE): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export interface FeaturableEvent {
  event_date: string;
  start_time?: string | null;
  status: string;
  public_visible: boolean;
}

/**
 * The next upcoming published open house, or null.
 * An open house stays featured through its own date and stops the day after.
 */
export function pickFeaturedOpenHouse<T extends FeaturableEvent>(events: T[], today: string): T | null {
  const candidates = events
    .filter((e) => e.public_visible)
    .filter((e) => (PUBLIC_OPEN_HOUSE_STATUSES as readonly string[]).includes(e.status))
    .filter((e) => e.event_date >= today)
    .sort((a, b) =>
      a.event_date === b.event_date
        ? (a.start_time ?? "").localeCompare(b.start_time ?? "")
        : a.event_date.localeCompare(b.event_date),
    );
  return candidates[0] ?? null;
}

function to12h(t: string) {
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  const suffix = hour >= 12 ? "PM" : "AM";
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${suffix}`;
}

/** e.g. "6:00 PM–7:00 PM" or "6:00 PM to approximately 7:00 PM". */
export function formatOpenHouseTime(start: string | null, end: string | null, endApproximate = false): string {
  if (!start) return "";
  if (!end) return to12h(start);
  return endApproximate
    ? `${to12h(start)} to approximately ${to12h(end)}`
    : `${to12h(start)}–${to12h(end)}`;
}

/** URL-safe unique slug for a new open house. */
export function buildOpenHouseSlug(title: string, date: string, suffix: string): string {
  const base = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50) || "open-house";
  return `${base}-${date}-${suffix}`;
}
